import React, { useMemo, useEffect, useCallback } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { useCategoriesQuery, GetAllGroupsQueryVariables, useGetAllGroupsLazyQuery, useGetMyAccountQuery } from 'graphql/types';
import { PaginatingItems, Container, SearchSidebar, SearchToolbarWrapper, Button } from 'components/elements';
import GroupCard from 'components/groupCard';
import { buildCategorySearchOptions } from 'utils/categorySearchOptions';
import qs from 'query-string';
import { useLocation, useHistory } from 'react-router-dom';
import { PageShell, PageHeader, PageHeaderRow, PageHeaderAction, PageTitle, GroupListWrapper, MyGroupsSections } from './groupsView.styled';

const pageSize = 6;

export default function GroupsView() {
  const [getGroups, { data: groupResult, loading: groupLoading }] = useGetAllGroupsLazyQuery();
  const { data: categoriesResult, loading: categoriesLoading } = useCategoriesQuery();
  const { data: accountData, loading: accountLoading } = useGetMyAccountQuery();
  const categories = useMemo(
    () => buildCategorySearchOptions(categoriesResult?.categories?.data ?? []),
    [categoriesResult],
  );

  const { search } = useLocation();
  const history = useHistory();
  const searchQuery = useMemo(() => qs.parse(search, { arrayFormat: 'bracket' }), [search]);

  const updatePageParams = (searchParams: any) => {
    history.push(`/groups?${qs.stringify(searchParams, { arrayFormat: 'bracket' })}`);
  };

  const getGroupPage = useCallback(
    (page: number) => {
      const searchFilter = { pagination: { page, pageSize }, sort: 'createdAt:desc' } as GetAllGroupsQueryVariables;
      const searchFilterProps = { status: { eq: 'approved' } } as any;
      if (searchQuery?.categories) {
        searchFilterProps.categories = { id: { in: searchQuery?.categories } };
      }
      if (searchQuery?.search) {
        const rawSearch = String(searchQuery.search).trim();
        const normalizedSearch = rawSearch.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        const orFilters = [
          { name: { containsi: rawSearch } },
          { slug: { containsi: rawSearch } },
        ];
        if (normalizedSearch && normalizedSearch !== rawSearch) {
          orFilters.push({ slug: { containsi: normalizedSearch } });
        }
        searchFilterProps.or = orFilters;
      }

      getGroups({ variables: { ...searchFilter, filters: searchFilterProps } });
    },
    [getGroups, searchQuery],
  );

  useEffect(() => {
    getGroupPage(1);
  }, [getGroupPage, getGroups, searchQuery]);

  const groupsData = groupResult?.groups.data || [];
  const pagination = groupResult?.groups?.meta?.pagination;

  const allLedGroups = accountData?.myAccount?.data?.attributes?.ledGroups?.data || [];
  const ledGroups = useMemo(
    () => allLedGroups.filter((group) => group.attributes?.status === 'approved'),
    [allLedGroups],
  );
  const joinedGroups = useMemo(() => {
    const ledGroupIds = new Set(allLedGroups.map((group) => group.id));
    const groups = accountData?.myAccount?.data?.attributes?.groups?.data || [];
    return groups.filter((group) => !ledGroupIds.has(group.id));
  }, [accountData, allLedGroups]);

  const hasSearchKeyword = Boolean((searchQuery.search as string)?.trim());

  const myGroupsSections = (
    <MyGroupsSections>
      <section>
        <PaginatingItems
          title="Groups I've created"
          items={ledGroups}
          renderList={(props) => <GroupCard {...props} editable />}
          loading={accountLoading}
          perView={3}
          fullWidthMobile
          pageSize={6}
          controlledPaginating
        />
      </section>

      <section>
        <PaginatingItems
          title="Joined Groups"
          items={joinedGroups}
          renderList={(props) => <GroupCard {...props} />}
          loading={accountLoading}
          perView={3}
          fullWidthMobile
          pageSize={6}
          controlledPaginating
        />
      </section>
    </MyGroupsSections>
  );

  const allGroupsSection = (
    <PaginatingItems
      title={hasSearchKeyword ? 'Search Results' : 'All Groups'}
      items={groupsData}
      loading={groupLoading || categoriesLoading}
      renderList={GroupCard}
      perView={3}
      fullWidthMobile
      pageSize={pagination?.pageSize}
      total={pagination?.total}
      current={pagination?.page}
      onChange={(page) => getGroupPage(page)}
    />
  );

  return (
    <Container>
      <PageShell>
        <PageHeader>
          <PageHeaderRow>
            <PageTitle>Explore Groups</PageTitle>
            <PageHeaderAction>
              <Button type="primary" onClick={() => history.push('/groups/create')}>
                <PlusOutlined />
                Create Group
              </Button>
            </PageHeaderAction>
          </PageHeaderRow>
        </PageHeader>
        <SearchToolbarWrapper>
          <SearchSidebar
            searchDefaultValue={(searchQuery.search as string) || ''}
            onSearch={(value) => updatePageParams({ ...searchQuery, search: value })}
            filterByCategories={[
              {
                title: 'category',
                key: 'category',
                onChange: (values) => updatePageParams({ ...searchQuery, categories: values }),
                options: categories,
                defaultValue: (searchQuery.categories as string[]) || [],
              },
            ]}
          />
        </SearchToolbarWrapper>

        <GroupListWrapper>
          {hasSearchKeyword ? (
            <>
              {allGroupsSection}
              {myGroupsSections}
            </>
          ) : (
            <>
              {myGroupsSections}
              {allGroupsSection}
            </>
          )}
        </GroupListWrapper>
      </PageShell>
    </Container>
  );
}
